import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../config/api";
import bgImage from "../../Images/login.svg";
import Logo from "../../Images/Logo.svg";
import ToastMessage from "../../config/ToastMessage";

export default function HavingTrouble() {
  const [formData, setFormData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Toast state
  const [toast, setToast] = useState({
    type: "",
    message: "",
  });

  // Handle Input Change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setToast({ type: "", message: "" });

    // Validation
    if (!formData.firstName.trim()) {
      setError("First Name is required");
      return;
    }
    if (!formData.lastName.trim()) {
      setError("Last Name is required");
      return;
    }
    if (!formData.email.trim()) {
      setError("Email is required");
      return;
    }
    
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError("Please enter a valid email address");
      return;
    }
    
    if (!formData.message.trim()) {
      setError("Message is required");
      return;
    }

    setLoading(true);

    try {
      // Prepare payload
      const payload = {
        first_name: formData.firstName.trim(),
        middle_name: formData.middleName.trim() || null,
        last_name: formData.lastName.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      };

      console.log("Sending payload:", payload); // Debug log

      const response = await axios.post(
        `${API_URL}/support/contact`,
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      console.log("Response:", response.data); // Debug log

      if (response.data.success) {
        setToast({
          type: "success",
          message: response.data.message || "Your message has been sent successfully. Our team will contact you soon.",
        });
        
        // Clear form
        setFormData({
          firstName: "",
          middleName: "",
          lastName: "",
          email: "",
          message: "",
        });

        // Redirect after 3 seconds
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else {
        setToast({
          type: "error",
          message: response.data.message || "Failed to send message",
        });
      }
    } catch (err) {
      console.error("Full error:", err); // Debug log
      console.error("Error response:", err.response); // Debug log
      
      let msg = "Something went wrong. Please try again.";
      if (err.response?.data) {
        const data = err.response.data;
        console.error("Error data:", data); // Debug log
        
        if (Array.isArray(data.detail) && data.detail.length > 0) {
          // Handle Pydantic validation errors
          msg = data.detail.map(e => e.msg).join(", ");
        } else if (typeof data.detail === "string") {
          msg = data.detail;
        } else if (data.message) {
          msg = data.message;
        }
      } else if (err.request) {
        // Network error
        msg = "Network error. Please check your connection and try again.";
      } else if (err.message) {
        msg = err.message;
      }
      
      setToast({
        type: "error",
        message: msg,
      });
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-end bg-gray-50"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Toast */}
      <ToastMessage
        type={toast.type}
        message={toast.message}
        onClose={() => setToast({ type: "", message: "" })}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40" />

      <div className="relative z-10 bg-white rounded-2xl shadow-[0_12px_24px_rgba(32,33,35,0.1)] border border-zinc-200 w-[90%] max-w-md p-8 flex flex-col gap-6 mr-20">
        
        {/* Logo */}
        <div className="flex justify-start mb-2">
          <img src={Logo} alt="SkillsMD Logo" className="w-36 h-auto" />
        </div>

        {/* Title */}
        <div className="text-left">
          <h1 className="text-zinc-800 text-lg font-bold font-['Roboto'] mb-2">
            Having Trouble?
          </h1>
          <p className="text-zinc-600 text-base font-normal font-['Roboto'] leading-6">
            Let us help you resolve it quickly
          </p>
        </div>

        {/* Form */}
        <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
          
          {/* First Name */}
          <div className="flex flex-col items-start gap-2">
            <label className="text-sm font-bold text-neutral-800">
              First Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter First Name"
              className="h-10 w-full px-3 bg-white rounded-md shadow-[0_0_5px_rgba(32,33,35,0.07)] outline outline-1 outline-zinc-200 text-sm text-zinc-700 focus:outline-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
            />
          </div>

          {/* Middle Name */}
          <div className="flex flex-col items-start gap-2">
            <label className="text-sm font-bold text-neutral-800">
              Middle Name
            </label>
            <input
              type="text"
              name="middleName"
              value={formData.middleName}
              onChange={handleChange}
              placeholder="Enter Middle Name"
              className="h-10 w-full px-3 bg-white rounded-md shadow-[0_0_5px_rgba(32,33,35,0.07)] outline outline-1 outline-zinc-200 text-sm text-zinc-700 focus:outline-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
            />
          </div>

          {/* Last Name */}
          <div className="flex flex-col items-start gap-2">
            <label className="text-sm font-bold text-neutral-800">
              Last Name <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter Last Name"
              className="h-10 w-full px-3 bg-white rounded-md shadow-[0_0_5px_rgba(32,33,35,0.07)] outline outline-1 outline-zinc-200 text-sm text-zinc-700 focus:outline-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
            />
          </div>

          {/* Email - NOW REQUIRED */}
          <div className="flex flex-col items-start gap-2">
            <label className="text-sm font-bold text-neutral-800">
              Email Address <span className="text-red-600">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email"
              className="h-10 w-full px-3 bg-white rounded-md shadow-[0_0_5px_rgba(32,33,35,0.07)] outline outline-1 outline-zinc-200 text-sm text-zinc-700 focus:outline-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5]"
            />
          </div>

          {/* Message */}
          <div className="flex flex-col items-start gap-2">
            <label className="text-sm font-bold text-neutral-800">
              Message <span className="text-red-600">*</span>
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Write your trouble"
              rows={4}
              className="w-full px-3 py-2 bg-white rounded-md shadow-[0_0_5px_rgba(32,33,35,0.07)] outline outline-1 outline-zinc-200 text-sm text-zinc-700 focus:outline-[#4AACC5] focus:ring-1 focus:ring-[#4AACC5] resize-none"
            />
          </div>

          {/* Error message */}
          {error && (
            <p className="text-red-600 text-xs font-normal font-['Roboto']">
              {error}
            </p>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className={`w-full px-5 py-2.5 rounded-md text-white text-sm font-bold shadow-sm transition-all ${
              loading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#4AACC5] hover:bg-[#3E95AC]"
            }`}
          >
            {loading ? "Sending..." : "Send Message"}
          </button>

        </form>

        {/* Privacy Notice */}
        <div className="self-stretch text-left">
          <p className="text-zinc-800 text-xs font-normal leading-4">
            By continuing, you agree to{" "}
            <span className="text-[#4AACC5] font-medium cursor-pointer">
              SkillsMD
            </span>{" "}
            Agreement or other agreement for, and the Privacy Notice. This site
            uses essential cookies. See our Cookie Notice for more information.
          </p>
        </div>

      </div>
    </div>
  );
}